import Decimal from "decimal.js";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { CashierTradingAccount, CurrencyPrimitive } from "@/api/cashier";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import refundCardTotalRow from "./RefundCardTotalRow.module.css";

type RefundCardTotalRowProps = {
  /** Number of currency entries in the card; the total row shows when > 1. */
  entriesCount: number;
  /** Sum of every entry's value expressed in the trading currency. */
  totalTrading: Decimal;
  /** True when a receiving currency (≠ trading currency) has been selected. */
  showConversionTotal: boolean;
  /** `totalTrading` converted into the selected receiving currency. */
  totalConversion: Decimal | null;
  /** Currently selected receiving currency code. */
  selectedCode: string | null;
  /**
   * Minimum withdrawal amount, denominated in the trading currency. Validated
   * against the summed trading-currency total of the card (the "Total
   * withdrawal" amount). When set and the total falls below it, the card is
   * invalid.
   */
  minimumTrading?: Decimal | null;
  minimumSelectedCurrency?: Decimal | null;
  selectedProcessingMinimumInTrading?: Decimal | null;
  maximumTrading?: Decimal | null;
  maximumSelectedCurrency?: Decimal | null;
  tradingAccount: CashierTradingAccount;
  currencies: Record<string, CurrencyPrimitive>;
  locale: string;
  onError?: (error: string) => void;
};

export const RefundCardTotalRow = ({
  entriesCount,
  totalTrading,
  showConversionTotal,
  totalConversion,
  selectedCode,
  minimumTrading,
  minimumSelectedCurrency,
  selectedProcessingMinimumInTrading,
  maximumTrading,
  maximumSelectedCurrency,
  tradingAccount,
  currencies,
  locale,
  onError,
}: RefundCardTotalRowProps) => {
  const { t } = useTranslation();
  const { formatAmount } = useFormatAmount();

  // The minimum is denominated in the trading currency, so validate the summed
  // trading-currency total (the "Total withdrawal" amount) against it. Rounded
  // to the trading currency's decimals to avoid float-noise false negatives.
  const tradingDecimals = tradingAccount.currency.decimals ?? 2;
  const roundedTotalTrading = totalTrading.toDecimalPlaces(
    tradingDecimals,
    Decimal.ROUND_HALF_UP
  );
  const belowMinimum = Boolean(
    minimumTrading &&
      roundedTotalTrading.gt(0) &&
      roundedTotalTrading.lt(minimumTrading)
  );
  const aboveMaximum = Boolean(
    maximumTrading && roundedTotalTrading.gt(maximumTrading)
  );

  const conversionCurrency =
    selectedCode &&
    selectedCode !== tradingAccount.currency.code &&
    currencies[selectedCode]
      ? { code: selectedCode, primitive: currencies[selectedCode] }
      : null;

  const minErrorMessage =
    belowMinimum && minimumTrading
      ? conversionCurrency && minimumSelectedCurrency
        ? t("withdrawals.methods.currency_conversion.error_min", {
            fromAmount: formatAmount(
              minimumSelectedCurrency.toNumber(),
              tradingAccount.currency,
              locale
            ),
            toAmount: formatAmount(
              selectedProcessingMinimumInTrading?.toNumber(),
              conversionCurrency.primitive,
              locale
            ),
          })
        : t("withdrawals.methods.currency_conversion.error_min_total", {
            amount: formatAmount(
              minimumTrading.toNumber(),
              tradingAccount.currency,
              locale
            ),
          })
      : "";

  const maxErrorMessage =
    aboveMaximum && maximumTrading
      ? conversionCurrency && maximumSelectedCurrency
        ? t("withdrawals.methods.currency_conversion.error_max", {
            fromAmount: formatAmount(
              maximumTrading.toNumber(),
              tradingAccount.currency,
              locale
            ),
            toAmount: formatAmount(
              maximumSelectedCurrency.toNumber(),
              conversionCurrency.primitive,
              locale
            ),
          })
        : t("withdrawals.methods.currency_conversion.error_max_total", {
            amount: formatAmount(
              maximumTrading.toNumber(),
              tradingAccount.currency,
              locale
            ),
          })
      : "";

  const errorMessage = minErrorMessage || maxErrorMessage;

  // Report the minimum-limit error to the parent so it can block submission.
  // Dedupe by the last reported message to avoid update loops since `onError`
  // is not memoised by the parent.
  const lastReported = useRef<string | null>(null);
  useEffect(() => {
    if (lastReported.current === errorMessage) return;
    lastReported.current = errorMessage;
    onError?.(errorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage]);

  useEffect(() => {
    // Clear any reported error when the row unmounts.
    return () => {
      if (lastReported.current) onError?.("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showRow =
    (entriesCount > 1 || showConversionTotal || belowMinimum || aboveMaximum) &&
    totalTrading.gt(0);

  if (!showRow) return null;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          paddingBlock: "var(--spacing-xs, 8px)",
          borderTop: "1px solid var(--color-stroke-primary, #03142e1a)",
          marginTop: "8px",
        }}
      >
        <span
          style={{
            fontSize: "var(--typography-size-14, 14px)",
            fontWeight: 600,
            color: "var(--color-text-primary, #03142e)",
            alignSelf: "baseline",
          }}
        >
          {t("withdrawals.methods.currency_conversion.total_withdrawal_label")}
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "2px",
          }}
        >
          <span
            style={{
              fontSize: "var(--typography-size-14, 14px)",
              fontWeight: 600,
              color: "var(--color-text-primary, #03142e)",
            }}
          >
            {formatAmount(
              totalTrading.toNumber(),
              tradingAccount.currency,
              locale
            )}
          </span>
          {showConversionTotal &&
            totalConversion !== null &&
            selectedCode &&
            currencies[selectedCode] && (
              <span
                style={{
                  fontSize: "var(--typography-size-12, 12px)",
                  color: "var(--color-text-tertiary)",
                }}
              >
                ≈
                {formatAmount(
                  totalConversion.toNumber(),
                  currencies[selectedCode],
                  locale
                )}
              </span>
            )}
        </div>
      </div>
      {errorMessage && (
        <div className={refundCardTotalRow.refundcardError + " ps-mt-2"}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </>
  );
};
